/**
 * 与应用搭建平台对接 http://confluence.crius.com/pages/viewpage.action?pageId=21110499
 */

import useMetaStore from '@/store/useMetaStore';
import useValuesStore from '@/store/useValuesStore';
import { useEffect } from 'react';
import { UsePlatformType, PermissionPoint } from './types';
import { AbilityPoint } from '@/const';
import { PageConfigType } from '@/asset/types';
import { ModuleTemplateType } from '@/asset/AssetTask/templates/Cascade/type';
import { PageType } from '../asset/types';

const usePlatform: UsePlatformType = ({ onLoad, props }) => {
  const { values } = useValuesStore();
  const { moduleList, taskInfo, pageConfig } = useMetaStore();
  useEffect(() => {
    if (moduleList.length > 0) {
      // 页面类型
      const pageType = props?.pageType ?? '';

      // 获取元数据
      const meta = getMeta({ moduleList, taskInfo, pageType });

      // 事件
      const events = getEvents({ pageType });
      // 权限点
      const auth = getAuth({ moduleList, pageConfig, taskInfo, pageType });
      console.log('permissionPoints', auth);
      const data = {
        meta,
        data: Object.keys(values || {}).map(key => ({
          key,
          fields: values?.[key],
        })),
        events,
        actions: [
          // {
          //   name: 'tabel刷新',
          //   fn: (current) => {
          //     console.log('table 刷新，current=' + current)
          //   },
          //   params: [
          //     { key: 'current', name: '设置页码' },
          //   ],
          // },
        ],
        auth,
      };
      onLoad && onLoad(data);
    }
  }, [values, moduleList, pageConfig]);
};

// 获取元数据
const getMeta = ({ moduleList, taskInfo, pageType }) => {
  const compFields = getCompFields({ pageType });
  const baseMeta = [
    {
      key: 'COMPONENT',
      name: '组件',
      fields: compFields,
    },
  ];
  const moduleMeta = moduleList
    .map(module => {
      const customFields = [
        {
          key: 'MODULE_HANDLE',
          name: '逻辑实体编码',
        },
        {
          key: '_id',
          name: '主键',
        },
      ];
      const fields = module.fields.map((field) => ({ key: field.name, name: field.label }));

      const isMain = taskInfo?.relationTree[0]?.code === module.handle;

      const name = isMain ? `${module.name}（主）` : module.name;

      return {
        key: module.handle,
        id: module.moduleID,
        name,
        fields: [
          ...customFields,
          ...fields,
        ],
        isMain,
      };
    })
    .sort((a: Record<string, any>, b: Record<string, any>) => {
      if (b.isMain) return 1;
      if (a.isMain) return -1;
    });

  return [
    ...baseMeta,
    ...moduleMeta,
  ];
};

// 公共组件字段
const commonCompFields = [
  {
    key: 'taskId',
    name: '任务ID',
  },
  {
    key: 'pageType',
    name: '组件类型',
  },
  {
    key: 'pageConfig',
    name: '组件配置',
  },
  {
    key: 'recordId',
    name: 'recordId',
  },
];

// 列表组件字段
const listCompFields = [
  {
    key: 'selectedRowKeys',
    name: '选中行',
  },
];

const getCompFields = ({ pageType }) => {
  switch (pageType) {
    case 'list':
      return [
        ...commonCompFields,
        ...listCompFields,
      ];
    case 'create':
      return [
        ...commonCompFields,
      ];
    case 'detail':
      return [
        ...commonCompFields,
      ];
    default:
      return [
        ...commonCompFields,
      ];
  }
};

// 获取事件
const getEvents = ({ pageType }) => {
  switch (pageType) {
    case 'list':
      return getListEvents();
    case 'create':
      return getCreateEvents();
    case 'detail':
      return getDetailEvents();
    default:
      return getListEvents();
  }
};

// 获取列表组件事件
const getListEvents = () => {
  return [
    {
      key: 'rowSelection',
      name: '行选择',
    },
  ];
};

// 获取列表组件事件
const getCreateEvents = () => {
  return [];
};

// 获取列表组件事件
const getDetailEvents = () => {
  return [];
};

const getAuth = ({ pageConfig, taskInfo, pageType }: {
  pageConfig: PageConfigType;
  taskInfo: ModuleTemplateType;
  pageType: string;
}) => {
  const { mainModuleId, modules } = taskInfo;
  const { freeBtnConfig = [], listPageConfig } = pageConfig;
  const mainModule = modules.find((m) => m.moduleID === mainModuleId);
  const rowModules = modules.filter((m) => m.moduleID !== mainModuleId);
  // 头模型相关权限点
  const mainPermissionPoint: PermissionPoint = {
    key: mainModule!.handle,
    name: mainModule!.name,
    abilitySet: [],
  };

  // 列表页 只保留头模型权限
  if (pageType === PageType.List) {
    // 列表查看权限
    mainPermissionPoint.abilitySet.push({ key: AbilityPoint.canSearch, name: '列表查看' });
    // 行数据详情查看权限
    (listPageConfig?.rowBtns?.includes('view-btns') || listPageConfig?.rowClickLink)
      && mainPermissionPoint.abilitySet.push({ key: AbilityPoint.canView, name: '行数据查看' });
    // 新增权限
    listPageConfig?.headerBtns?.includes('add-btns')
      && mainPermissionPoint.abilitySet.push({ key: AbilityPoint.canCreate, name: '创建' });
    // 编辑权限
    listPageConfig?.rowBtns?.includes('edit-btns')
      && mainPermissionPoint.abilitySet.push({ key: AbilityPoint.canUpdate, name: '编辑' });
    // 删除权限
    (listPageConfig?.headerBtns?.includes('delete-btns') || listPageConfig?.rowBtns?.includes('delete-btns'))
      && mainPermissionPoint.abilitySet.push({ key: AbilityPoint.canDelete, name: '删除' });
    // 导出权限
    listPageConfig?.headerBtns?.includes('export-btns')
      && mainPermissionPoint.abilitySet.push({ key: AbilityPoint.canExport, name: '导出' });
    // 自定义按钮权限
    freeBtnConfig.forEach((btn: any) => {
      mainPermissionPoint.abilitySet.push({
        key: `${btn.id || btn.text}`,
        name: `${btn.text}`,
      });
    });
    return [mainPermissionPoint];
  }
  // 创建页/编辑页 只保留头模型权限
  if (pageType === PageType.Create || pageType === PageType.Edit) {
    mainPermissionPoint.abilitySet.push(
      { key: AbilityPoint.canCreate, name: '创建' },
      { key: AbilityPoint.canUpdate, name: '编辑' },
    );
    // 行内按钮，表格顶部的按钮都不要，底部按钮要
    freeBtnConfig.forEach((btn: any) => {
      if (btn.position === 'pageBottom') {
        mainPermissionPoint.abilitySet.push({
          key: `${btn.id || btn.text}`,
          name: `${btn.text}`,
        });
      }
    });
    return [mainPermissionPoint];
  }
  // 详情页 保留各个模型权限
  if (pageType === PageType.Detail) {
    mainPermissionPoint.abilitySet.push(
      { key: AbilityPoint.canView, name: '查看' },
    );
    // 行模型相关权限点，目前只定义是否可查看
    const rowPermissionPoints: PermissionPoint[] = rowModules.map((m) => ({
      key: m.handle,
      name: m.name,
      abilitySet: [
        { key: AbilityPoint.canView, name: '查看' },
      ],
    }));
    // 所有按钮都需要权限控制
    freeBtnConfig.forEach((btn) => {
      const linkMoudle = rowPermissionPoints.find((row) => row.key === btn['position-module']);
      (linkMoudle || mainPermissionPoint).abilitySet.push({
        key: `${btn.id || btn.text}`,
        name: `${btn.text}`,
      });
    });

    return [mainPermissionPoint, ...rowPermissionPoints];
  }
};


export default usePlatform;
